import styles from './index.module.css'

import { LuCircle } from '@react-icons/all-files/lu/LuCircle'
import { LuSparkles } from '@react-icons/all-files/lu/LuSparkles'
import cn from 'clsx'
import React from 'react'

import { COLORS } from '@disco/utils/color-indicators'

import { Badge } from 'components/ds'

type Props = {
  name: string
  /** Business-defined color indicator (e.g. red = one-stop). */
  color?: string | null
  /** Marks a tag applied by auto-tagging — renders the AI sparkles. */
  ai?: boolean
  selected: boolean
  /** Plays the pop-in animation for a just-created tag. */
  fresh?: boolean
  /** Radix Badge size. Defaults to "2" so every current call site keeps
   * its look. */
  size?: '1' | '2' | '3'
  onToggle: () => void
}

/**
 * Toggleable tag pill used by both the category grid and the global search
 * results, so selection reads the same everywhere.
 *
 * Every chip stays neutral gray regardless of type — AI-applied tags are
 * marked only by the sparkles icon, copy stays uncolored to keep the grid
 * quiet. Selection switches the fill to Radix's gray outline (white
 * background, gray ring) instead of changing color.
 */
const TagChip = ({
  name,
  color,
  ai,
  selected,
  fresh,
  size = '2',
  onToggle,
}: Props) => (
  <Badge
    asChild
    size={size}
    variant={selected ? 'outline' : 'soft'}
    color="gray"
    className={cn(styles.chip, { [styles.chipFresh]: fresh })}
  >
    <button type="button" aria-pressed={selected} onClick={onToggle}>
      {ai && <LuSparkles size={12} className={styles.chipAi} />}
      {name}
      {color && (
        <LuCircle
          size={8}
          fill={COLORS[color]}
          stroke="none"
          className={styles.chipDot}
        />
      )}
    </button>
  </Badge>
)

export default TagChip
